import React, { useEffect, useRef, useState } from 'react';
import AIChatHeader from './AIChatHeader';
import AIChatMessages from './AIChatMessages';
import AIChatComposer from './AIChatComposer';
import useAutoCreateChat from './hooks/useAutoCreateChat';
import { useAI } from '../../context/AIContext';
import { useEdit } from '../../context/EditContext';
import { typography, surfaces, textColors } from '../../../design-system/tokens';

/**
 * AIChatSection Component
 *
 * Base component for the AI chat section in EditPanel.
 * Provides collapsible interface with smooth animations and tab navigation.
 *
 * Props:
 * - collapsed: boolean - whether the section is collapsed
 * - onToggle: function - callback when collapse/expand is clicked
 * - websiteId: number - current website ID
 * - fillParent: boolean - if true, fills parent height; if false, uses fixed height (default: false)
 */
const AIChatSection = ({ collapsed, onToggle, websiteId, fillParent = false }) => {
  // Auto-create first chat if none exists
  useAutoCreateChat(websiteId);

  const { currentChat, loading } = useAI();
  const { selectedElement } = useEdit();
  const [ephemeralMessages, setEphemeralMessages] = useState([]);
  const [composerDraft, setComposerDraft] = useState('');
  const composerInputRef = useRef(null);
  const isReviewMode = currentChat?.chat_type === 'site_review';
  
  // Constrain height by viewport so the chat never grows past the screen.
  // This also guarantees the messages area can scroll.
  // Note: dvh handles mobile browser UI better than vh.
  const expandedHeight = fillParent ? '70dvh' : 'min(70dvh, 420px)';
  const style = collapsed
    ? { height: 0, opacity: 0 }
    : { height: expandedHeight, maxHeight: '70dvh', opacity: 1 };

  const addEphemeralMessage = (msg) => {
    if (!msg?.id) return;
    setEphemeralMessages((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const filtered = list.filter((m) => String(m.id) !== String(msg.id));
      return [...filtered, msg];
    });
  };

  const removeEphemeralMessage = (id) => {
    if (!id) return;
    setEphemeralMessages((prev) => (Array.isArray(prev) ? prev : []).filter((m) => String(m.id) !== String(id)));
  };

  const focusComposerInput = () => {
    requestAnimationFrame(() => {
      composerInputRef.current?.focus?.();
    });
  };

  const handlePrefillDraft = (nextValue) => {
    setComposerDraft(nextValue || '');
    focusComposerInput();
  };

  useEffect(() => {
    setComposerDraft('');
  }, [currentChat?.id]);

  return (
    <div
      data-tutorial-id="ai-chat"
      data-testid="ai-chat-section-root"
      className={`ai-chat-section ${surfaces.primary} flex flex-col min-h-0`}
    >
      {/* Header with tabs and collapse/expand control */}
      <AIChatHeader
        collapsed={collapsed}
        onToggle={onToggle}
        websiteId={websiteId}
      />

      {/* Expandable content with CSS transition */}
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out flex flex-col min-h-0"
        style={style}
      >
        <div className="flex flex-col h-full min-h-0">
          {/* Chat content - flex-1 to fill remaining space */}
          {loading && !currentChat ? (
            <div className={`flex-1 flex items-center justify-center ${textColors.secondary} ${typography.bodySmall} p-4`}>
              Loading chat...
            </div>
          ) : currentChat ? (
            <div
              data-testid="ai-chat-active-session"
              data-review-mode={isReviewMode ? 'true' : 'false'}
              className={`relative flex flex-col flex-1 min-h-0 ${
                isReviewMode
                  ? 'mx-2 mb-2 rounded-2xl overflow-hidden bg-gradient-to-b from-green-400/5 to-transparent shadow-[0_0_28px_rgba(74,222,128,0.22),inset_0_0_24px_rgba(74,222,128,0.14)]'
                  : ''
              }`}
            >
              {isReviewMode ? (
                <div
                  data-testid="ai-review-background-layer"
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 bg-green-400/20"
                />
              ) : null}
              <div className="relative z-10 flex flex-col flex-1 min-h-0">
                {/* Messages area - handles its own scrolling */}
                <AIChatMessages
                  chat={currentChat}
                  extraMessages={ephemeralMessages}
                  onPrefillDraft={handlePrefillDraft}
                  onFocusComposer={focusComposerInput}
                  isReviewMode={isReviewMode}
                />
                <div className="flex-shrink-0">
                  <AIChatComposer
                    chat={currentChat}
                    selectedElement={selectedElement}
                    addEphemeralMessage={addEphemeralMessage}
                    removeEphemeralMessage={removeEphemeralMessage}
                    messageCount={(Array.isArray(currentChat?.messages) ? currentChat.messages.length : 0) + ephemeralMessages.length}
                    draftValue={composerDraft}
                    onDraftChange={setComposerDraft}
                    onFocusInput={focusComposerInput}
                    textareaRef={composerInputRef}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className={`flex-1 flex items-center justify-center ${textColors.secondary} ${typography.bodySmall} p-4`}>
              <div className="text-center">
                <p className="mb-2">No chat selected</p>
                <p className={`${typography.caption} ${textColors.muted}`}>
                  Select a chat from the tabs above or create a new one
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIChatSection;
